import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Users,
  Plus,
  Pencil,
  Trash2,
  RefreshCw,
  Save,
  X,
  Eye,
  EyeOff,
  ShieldCheck,
  AlertTriangle,
} from "lucide-react";
import api, { formatApiError } from "../../lib/api";

/**
 * Admin → Organizers tab.
 *
 * CRUD editor for the NCUA Form 9500 organizing group. The public `/organizers`
 * page renders only `active: true` rows.
 */
export default function OrganizersTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // row or {} for new
  const minRequired = 7;

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/organizers");
      setRows(r.data?.rows || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load organizers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const activeCount = rows.filter((r) => r.active).length;
  const meetsMinimum = activeCount >= minRequired;

  const save = async (payload) => {
    try {
      if (editing?.id) {
        await api.put(`/admin/organizers/${editing.id}`, payload);
        toast.success("Organizer updated");
      } else {
        await api.post("/admin/organizers", payload);
        toast.success("Organizer added");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Remove ${row.name} from the organizing group?`)) return;
    try {
      await api.delete(`/admin/organizers/${row.id}`);
      toast.success("Organizer removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-organizers-tab">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow flex items-center gap-2">
            <Users size={12} className="text-bdu-gold" /> NCUA Form 9500 · Organizing Group Roster
          </div>
          <h3 className="mt-2 font-serif text-2xl text-bdu-cream">
            Organizing Group
          </h3>
          <p className="mt-1 text-xs text-bdu-cream/55 max-w-2xl leading-relaxed">
            NCUA Chapter 2 requires a documented organizing group of at least{" "}
            <span className="text-bdu-cream font-semibold">{minRequired}</span>{" "}
            individuals. Active organizers below are published on the public{" "}
            <code className="text-bdu-gold">/organizers</code> page.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div
            className={`flex items-center gap-2 border px-3 py-1.5 text-xs ${
              meetsMinimum
                ? "border-bdu-gold/50 bg-bdu-gold/5 text-bdu-gold"
                : "border-bdu-red/40 bg-bdu-red/5 text-bdu-red"
            }`}
            data-testid="admin-organizers-count-badge"
          >
            {meetsMinimum ? <ShieldCheck size={13} /> : <AlertTriangle size={13} />}
            <span className="font-semibold">{activeCount}</span> / {minRequired} active
          </div>
          <button
            onClick={load}
            className="btn-ghost text-xs flex items-center gap-2"
            data-testid="admin-organizers-refresh-btn"
          >
            <RefreshCw size={13} /> Refresh
          </button>
          <button
            onClick={() =>
              setEditing({ name: "", role: "", bio: "", photo_url: "", linkedin_url: "", email: "", order: 100, active: true })
            }
            className="btn-gold text-xs flex items-center gap-2"
            data-testid="admin-organizers-add-btn"
          >
            <Plus size={14} /> Add Organizer
          </button>
        </div>
      </div>

      {/* Edit panel */}
      {editing && (
        <OrganizerForm
          initial={editing}
          onCancel={() => setEditing(null)}
          onSave={save}
        />
      )}

      {/* List */}
      {loading ? (
        <div className="py-16 text-center text-bdu-cream/50">Loading organizers…</div>
      ) : rows.length === 0 ? (
        <div className="border border-bdu-gold/15 py-16 text-center text-bdu-cream/50">
          No organizers added yet.
        </div>
      ) : (
        <div className="space-y-3" data-testid="admin-organizers-list">
          {rows.map((o) => (
            <OrganizerRow
              key={o.id}
              organizer={o}
              onEdit={() => setEditing(o)}
              onDelete={() => remove(o)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function OrganizerRow({ organizer, onEdit, onDelete }) {
  const initials = (organizer.name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("");
  return (
    <div
      className={`flex items-start gap-4 border p-4 ${
        organizer.active ? "border-bdu-gold/25 bg-black/40" : "border-bdu-cream/10 bg-black/20 opacity-70"
      }`}
      data-testid={`admin-organizer-row-${organizer.id}`}
    >
      {organizer.photo_url ? (
        <img
          src={organizer.photo_url}
          alt={organizer.name}
          className="w-14 h-14 rounded-full object-cover border border-bdu-gold/40 flex-shrink-0"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      ) : (
        <div className="w-14 h-14 rounded-full flex items-center justify-center border border-bdu-gold/40 bg-bdu-gold/10 text-bdu-gold font-serif text-lg flex-shrink-0">
          {initials || <Users size={16} />}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-serif text-base text-bdu-cream">
            {organizer.name}
          </span>
          <span className="text-[10px] uppercase tracking-widest text-bdu-cream/40">
            Order {organizer.order}
          </span>
          <span
            className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border ${
              organizer.active
                ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                : "border-bdu-cream/20 text-bdu-cream/50"
            }`}
          >
            {organizer.active ? (
              <span className="inline-flex items-center gap-1"><Eye size={10} /> Public</span>
            ) : (
              <span className="inline-flex items-center gap-1"><EyeOff size={10} /> Hidden</span>
            )}
          </span>
        </div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-bdu-gold mt-1">
          {organizer.role}
        </div>
        {organizer.bio ? (
          <p className="mt-2 text-xs text-bdu-cream/55 line-clamp-2 leading-relaxed">
            {organizer.bio}
          </p>
        ) : null}
      </div>
      <div className="flex flex-col gap-2">
        <button
          onClick={onEdit}
          className="btn-ghost text-xs flex items-center gap-1.5"
          data-testid={`admin-organizer-edit-btn-${organizer.id}`}
        >
          <Pencil size={12} /> Edit
        </button>
        <button
          onClick={onDelete}
          className="text-xs text-bdu-cream/50 hover:text-bdu-red transition-colors flex items-center gap-1.5 px-3 py-1.5"
          data-testid={`admin-organizer-delete-btn-${organizer.id}`}
        >
          <Trash2 size={12} /> Delete
        </button>
      </div>
    </div>
  );
}

function OrganizerForm({ initial, onCancel, onSave }) {
  const [form, setForm] = useState({
    name: initial.name || "",
    role: initial.role || "",
    bio: initial.bio || "",
    photo_url: initial.photo_url || "",
    linkedin_url: initial.linkedin_url || "",
    email: initial.email || "",
    order: initial.order ?? 100,
    active: initial.active !== false,
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) =>
    setForm((p) => ({
      ...p,
      [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.role.trim()) {
      toast.error("Name and Role are required");
      return;
    }
    setSaving(true);
    try {
      await onSave({
        ...form,
        order: Number(form.order) || 100,
        email: form.email.trim() || null,
        photo_url: form.photo_url.trim() || null,
        linkedin_url: form.linkedin_url.trim() || null,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="border border-bdu-gold/40 bg-black/60 p-6"
      data-testid="admin-organizer-form"
    >
      <div className="flex items-center justify-between mb-5">
        <h4 className="font-serif text-lg text-bdu-cream">
          {initial.id ? "Edit organizer" : "Add organizer"}
        </h4>
        <button
          type="button"
          onClick={onCancel}
          className="text-bdu-cream/50 hover:text-bdu-red"
          aria-label="Cancel"
          data-testid="admin-organizer-form-cancel"
        >
          <X size={16} />
        </button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Name" required>
          <input
            type="text"
            value={form.name}
            onChange={set("name")}
            required
            maxLength={120}
            data-testid="admin-organizer-field-name"
            className="w-full bg-bdu-surface2 border border-bdu-gold/20 text-bdu-cream text-sm px-3 py-2.5 focus:border-bdu-gold outline-none"
          />
        </Field>
        <Field label="Role / Title" required>
          <input
            type="text"
            value={form.role}
            onChange={set("role")}
            required
            maxLength={120}
            placeholder="Chairperson · Lead Organizer"
            data-testid="admin-organizer-field-role"
            className="w-full bg-bdu-surface2 border border-bdu-gold/20 text-bdu-cream text-sm px-3 py-2.5 focus:border-bdu-gold outline-none"
          />
        </Field>
        <Field label="Photo URL (optional)" className="sm:col-span-2">
          <input
            type="url"
            value={form.photo_url}
            onChange={set("photo_url")}
            maxLength={600}
            placeholder="https://… (public image URL, 1:1 crop recommended)"
            data-testid="admin-organizer-field-photo"
            className="w-full bg-bdu-surface2 border border-bdu-gold/20 text-bdu-cream text-sm px-3 py-2.5 focus:border-bdu-gold outline-none"
          />
        </Field>
        <Field label="LinkedIn URL (optional)">
          <input
            type="url"
            value={form.linkedin_url}
            onChange={set("linkedin_url")}
            maxLength={400}
            placeholder="https://www.linkedin.com/in/…"
            data-testid="admin-organizer-field-linkedin"
            className="w-full bg-bdu-surface2 border border-bdu-gold/20 text-bdu-cream text-sm px-3 py-2.5 focus:border-bdu-gold outline-none"
          />
        </Field>
        <Field label="Contact email (optional)">
          <input
            type="email"
            value={form.email}
            onChange={set("email")}
            placeholder="organizer@blackdollarsunited.org"
            data-testid="admin-organizer-field-email"
            className="w-full bg-bdu-surface2 border border-bdu-gold/20 text-bdu-cream text-sm px-3 py-2.5 focus:border-bdu-gold outline-none"
          />
        </Field>
        <Field label="Bio" className="sm:col-span-2">
          <textarea
            value={form.bio}
            onChange={set("bio")}
            maxLength={800}
            rows={3}
            placeholder="1–3 sentence bio and NCUA role responsibilities."
            data-testid="admin-organizer-field-bio"
            className="w-full bg-bdu-surface2 border border-bdu-gold/20 text-bdu-cream text-sm px-3 py-2.5 focus:border-bdu-gold outline-none resize-y"
          />
        </Field>
        <Field label="Order (lower shows first)">
          <input
            type="number"
            min="0"
            max="9999"
            value={form.order}
            onChange={set("order")}
            data-testid="admin-organizer-field-order"
            className="w-full bg-bdu-surface2 border border-bdu-gold/20 text-bdu-cream text-sm px-3 py-2.5 focus:border-bdu-gold outline-none"
          />
        </Field>
        <div className="flex items-end">
          <label
            className="flex items-center gap-2 text-sm text-bdu-cream/80 cursor-pointer"
            data-testid="admin-organizer-field-active-wrapper"
          >
            <input
              type="checkbox"
              checked={form.active}
              onChange={set("active")}
              className="accent-bdu-gold"
              data-testid="admin-organizer-field-active"
            />
            Active (visible on public /organizers page)
          </label>
        </div>
      </div>

      <div className="mt-5 flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="btn-ghost text-xs"
          data-testid="admin-organizer-form-cancel-btn"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="btn-gold text-xs flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="admin-organizer-form-save-btn"
        >
          {saving ? (
            <>
              <RefreshCw size={13} className="animate-spin" /> Saving…
            </>
          ) : (
            <>
              <Save size={13} /> Save Organizer
            </>
          )}
        </button>
      </div>
    </form>
  );
}

function Field({ label, children, className = "", required }) {
  return (
    <label className={`text-xs text-bdu-cream/65 flex flex-col gap-1.5 ${className}`}>
      <span>
        {label}
        {required ? <span className="text-bdu-red"> *</span> : null}
      </span>
      {children}
    </label>
  );
}
